import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ContextVerbiage } from 'app/profile/models/supplier.model';
import { SupplierTaskService } from 'app/profile/services/supplier-task.service';
import { OnboardingState } from 'app/profile/store/state/onboarding.state';
import { I18nService } from 'app/shared/services/i18n.service';
import * as MasterDataSelectors from "../../../store/selectors/masterdata.selector";
import { SupplierTaskResponse, SupplierTaskStep } from 'app/profile/models/suppliertasks.model';
import { OnboardingDocument, OnboardingTab, TaskStatus } from 'app/profile/models/onboarding.model';
import { Subscription } from 'rxjs';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { FileUploadService } from 'app/common/services/fileUpload.service';
import { OnboardingDocumentsService } from 'app/profile/services/onboarding-document.service';
import * as FileSaver from 'file-saver';
import { TslToastrService } from '@tesla/ng-tds';

@Component({
  selector: 'app-detail-task-wrapper',
  templateUrl: './detail-task-wrapper.component.html',
  styleUrls: ['./detail-task-wrapper.component.scss']
})
export class DetailTaskWrapperComponent implements OnInit, OnDestroy {

  @Input('supplierCode') set _supplierCode(val: string) { if (val && val.trim() != "") this.supplierCode = val; };
  @Input('taskId') set _taskId(val: number) { if (val) this.taskId = val; };
  @Input('isSidePanelMode') isSidePanelMode: boolean = false;
  @Input() isReactivation: boolean = false;
  @Input() enableExistSelection: boolean = false;
  @Output() onSave: EventEmitter<boolean> = new EventEmitter<boolean>();

  onboardingTab: OnboardingTab = OnboardingTab.DETAILS;
  supplierCode: string = null;
  taskId: number;
  rejectionDetails: any;
  taskStatus: string;
  contextVerbiage: ContextVerbiage;
  taskInfo: SupplierTaskStep;
  requestType: string;
  isLoaded: boolean = false;
  supplierDetail: any;
  getTaskStepSub: Subscription;
  fileUploadSub: Subscription;

  detailsForm: FormGroup;
  isLegalNameValid: FormControl;
  legalName: FormControl;
  isOnlineCommunication: FormControl;
  companyAnnouncementWebsite: FormControl;
  isDBANameValid: FormControl;
  DBAName: FormControl;
  file: OnboardingDocument = {};
  documentErrorMessage: string;
  oldLegalName: string;
  oldDBAName: string;
  oldWebsite: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public translate: I18nService,
    private taskService: SupplierTaskService,
    private onboardingStore: Store<OnboardingState>,
    private fileUploadService: FileUploadService,
    private onboardingDocumentsService: OnboardingDocumentsService,
    private toaster: TslToastrService,
  ) { }

  ngOnInit(): void {
    this.route?.parent?.params?.subscribe(params => {
      if (params?.code && params?.portalTaskId) {
        this.supplierCode = params.code;
        this.taskId = params.portalTaskId;
      }
    });

    this.onboardingStore.select(MasterDataSelectors.SelectContextVerbiage).subscribe(contextVerbiage => this.contextVerbiage = contextVerbiage);

    // get task details by task type.
    this.getTaskStepSub = this.taskService.getSupplierTaskStep(this.supplierCode, this.taskId, this.onboardingTab).subscribe(taskInfo => {
      if (taskInfo && taskInfo.taskConfiguration) {
        this.taskInfo = taskInfo;
        this.oldLegalName = this.taskInfo?.taskRequestMetadata?.legalName;
        this.oldDBAName = this.taskInfo?.taskRequestMetadata?.dbaName;
        this.requestType = this.taskInfo?.taskConfiguration?.SupplierRequestType;
        if(this.requestType) this.requestType = this.requestType[0].toLocaleLowerCase() + this.requestType.substring(1);
        this.taskStatus = taskInfo.taskStatus;
        this.rejectionDetails = taskInfo?.rejectionDetails;
        if (this.taskStatus != TaskStatus.Completed && taskInfo.portalTaskResponse != null) {
          this.supplierDetail = {};
          for(let key in taskInfo.portalTaskResponse) {
            let newKey = key[0].toLocaleLowerCase() + key.substring(1);
            this.supplierDetail[newKey] = taskInfo.portalTaskResponse[key];
          }
        }
        this.initFormControls()
        this.isLoaded = true;
      }
    });
  }

  initFormControls() {
    this.isLegalNameValid = new FormControl(null, [Validators.required]);
    this.legalName = new FormControl(null, []);
    this.isOnlineCommunication = new FormControl(null, []);
    this.companyAnnouncementWebsite = new FormControl(null, []);
    this.isDBANameValid = new FormControl(null, [Validators.required]);
    this.DBAName = new FormControl(null, []);

    this.detailsForm = new FormGroup({
      isLegalNameValid: this.isLegalNameValid,
      legalName: this.legalName,
      isOnlineCommunication: this.isOnlineCommunication,
      companyAnnouncementWebsite: this.companyAnnouncementWebsite,
      isDBANameValid: this.isDBANameValid,
      DBAName: this.DBAName
    });

    this.isLegalNameValid.valueChanges.subscribe(value => {
      if(!value) {
        this.legalName.setValidators([Validators.required, this.checkSameMatchValidator(this.oldLegalName)]);
        this.isOnlineCommunication.setValidators([Validators.required]);
      } else {
        this.legalName.setValidators([]);
        this.isOnlineCommunication.setValidators([]);
        this.legalName.setValue(null);
        this.isOnlineCommunication.setValue(null);
        this.file = {};
      }
      this.detailsForm.updateValueAndValidity();
    });

    this.isDBANameValid.valueChanges.subscribe(value => {
      if(!value) {
        this.DBAName.setValidators([Validators.required, this.checkSameMatchValidator(this.oldDBAName)]);
      } else {
        this.DBAName.setValidators([]);
        this.DBAName.setValue(null);
      }
      this.detailsForm.updateValueAndValidity();
    });

    this.isOnlineCommunication.valueChanges.subscribe(value => {
      if(value) {
        this.companyAnnouncementWebsite.setValidators([Validators.required, this.checkIsWebsite()]);
      } else {
        this.companyAnnouncementWebsite.setValidators([]);
        this.companyAnnouncementWebsite.setValue(null);
      }
      this.detailsForm.updateValueAndValidity();
    });

    this.setControlValues();
  }

  private setControlValues() {
    if (this.supplierDetail) {
        this.isLegalNameValid.setValue(this.supplierDetail.isLegalNameValid, { emitEvent: true });
        this.legalName.setValue(this.supplierDetail.legalName, { emitEvent: false });
        this.isOnlineCommunication.setValue(this.supplierDetail.isOnlineCommunication, { emitEvent: true });
        this.companyAnnouncementWebsite.setValue(this.supplierDetail.companyAnnouncementWebsite, { emitEvent: false });
        this.isDBANameValid.setValue(this.supplierDetail.isDBANameValid, { emitEvent: true });
        this.DBAName?.setValue(this.supplierDetail.dBAName);
        if(this.supplierDetail.documentInfos?.length) this.file = this.supplierDetail.documentInfos[0];
    }
  }

  checkSameMatchValidator(str: string): ValidatorFn {
    return (control:AbstractControl) : ValidationErrors | null => {
        const value = control.value?.trim();
        if (!value) {
            return {required: true};
        }
        return value == str?.trim() ? {duplicateString: true}: null;
    }
  }

  checkIsWebsite(): ValidatorFn {
    return (control:AbstractControl) : ValidationErrors | null => {
      const value = control.value?.trim();
      if (!value) {
          return {required: true};
      }

      // regex pattren to validate website url's
      const websiteRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w.-]*)\/?$/;
      const isValid = websiteRegex.test(value);
      return isValid ? null : {notValidWebsite: true};
    }
  }

  onSubmit() {
    this.detailsForm?.markAllAsTouched();
    if(!this.file?.File && !this.file.DocumentID && !this.detailsForm?.value?.isLegalNameValid) {
      this.documentErrorMessage = "Please upload one file.";
      return;
    }
    if(!this.detailsForm?.valid) return;
    if(!this.detailsForm?.value?.isLegalNameValid) {
      if(!this.file.DocumentID && this.file.File?.name) {
        this.fileUploadSub = this.fileUploadService.uploadFileToServer([this.file.File], this.supplierCode, { 'DocumentType': "NAMECHNG" }).subscribe(resp => {
          //  Map response data to document data
          this.file.DocumentID = resp['documentID'] || resp.DocumentID;
          this.file.DocumentVersion = resp['documentVersion'] || resp.DocumentVersion;
          this.file.FileName = resp['fileName'] || resp.FileName;
          this.file.FileNameGuid = resp['fileNameGuid'] || resp.FileNameGuid;
          this.file.DocumentType = "NAMECHNG"
          this.file.UploadedDate = new Date();
          delete this.file.File;
          let body = {...this.detailsForm.value};
          if(this.file.FileName && this.file.DocumentID) {
            body["documents"] = [this.file.DocumentID];
            body["documentInfos"] = [this.file];
          }
          let response: SupplierTaskResponse = {
            id: this.taskId,
            supplierCode: this.supplierCode,
            taskResponseJson: JSON.stringify(body)
          };
          this.taskService.postSupplierTaskStepResponse(this.supplierCode, response).subscribe(res => {
            if (res) {
              this.toaster.success(this.translate.get('Submitted successfully!'));
              if (this.isSidePanelMode) this.onSave.emit(true);
              else this.router.navigate([`${this.supplierCode}`, "Onboarding", "Home"]);
            }
            else {
              this.toaster.error(this.translate.get('Something went wrong!'));
            }
          });
        });
      } else {
        let body = {...this.detailsForm.value};
        if(this.file.FileName && this.file.DocumentID) {
          body["documents"] = [this.file.DocumentID];
          body["documentInfos"] = [this.file];
        }
        let response: SupplierTaskResponse = {
          id: this.taskId,
          supplierCode: this.supplierCode,
          taskResponseJson: JSON.stringify(body)
        };
        this.taskService.postSupplierTaskStepResponse(this.supplierCode, response).subscribe(res => {
          if (res) {
            this.toaster.success(this.translate.get('Submitted successfully!'));
            if (this.isSidePanelMode) this.onSave.emit(true);
            else this.router.navigate([`${this.supplierCode}`, "Onboarding", "Home"]);
          }
          else {
            this.toaster.error(this.translate.get('Something went wrong!'));
          }
        });
      }
    } else {
      let body = {...this.detailsForm.value};
      let response: SupplierTaskResponse = {
        id: this.taskId,
        supplierCode: this.supplierCode,
        taskResponseJson: JSON.stringify(body)
      };
      this.taskService.postSupplierTaskStepResponse(this.supplierCode, response).subscribe(res => {
        if (res) {
          this.toaster.success(this.translate.get('Submitted successfully!'));
          if (this.isSidePanelMode) this.onSave.emit(true);
          else this.router.navigate([`${this.supplierCode}`, "Onboarding", "Home"]);
        }
        else {
          this.toaster.error(this.translate.get('Something went wrong!'));
        }
      });
    }

  }

  navigateToHome() {
    this.router.navigate([`${this.supplierCode}`, "Onboarding", "Home"]);
  }

  onFileChange(event) {
    if(event?.length) {
      this.file.File = event[0];
      this.documentErrorMessage = null;
    } else if (!this.file?.File) this.documentErrorMessage = "Please upload one file.";
  }

  removeFile() {
    this.file = {};
  }

  downloadFile(file: OnboardingDocument) {
    if (file?.DocumentID && file?.DocumentVersion) {
      this.onboardingDocumentsService.downloadDocument(file.DocumentID, this.supplierCode, file.DocumentVersion).subscribe(resultBlob => {
        FileSaver.saveAs(resultBlob, file.FileName || file.DocumentID);
      });
    } else if (file?.File) {
      FileSaver.saveAs(file.File, file.FileName || file.File.name);
    }
  }

  ngOnDestroy(): void {
    if(this.getTaskStepSub) this.getTaskStepSub.unsubscribe();
    if(this.fileUploadSub) this.fileUploadSub.unsubscribe();
  }
}
